

import { useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useColor } from "../../../../components/ui/ColorContext";

// ─── Sound Functions ───

function playCrystalForm(ctx: AudioContext) {
  const now = ctx.currentTime;
  const freqs = [1200, 1500, 1800, 2200, 2600];
  freqs.forEach((freq, i) => {
    const t = now + i * 0.1;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.06, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(t); osc.stop(t + 0.32);
  });
  const lock = ctx.createOscillator();
  const lockGain = ctx.createGain();
  lock.type = 'sine';
  lock.frequency.setValueAtTime(3200, now + 0.5);
  lockGain.gain.setValueAtTime(0, now + 0.5);
  lockGain.gain.linearRampToValueAtTime(0.08, now + 0.52);
  lockGain.gain.exponentialRampToValueAtTime(0.001, now + 0.9);
  lock.connect(lockGain); lockGain.connect(ctx.destination);
  lock.start(now + 0.5); lock.stop(now + 0.92);
}

function playHoloMaterialize(ctx: AudioContext) {
  const now = ctx.currentTime;
  [400, 600, 800, 1000].forEach((freq, i) => {
    const t = now + i * 0.04;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(freq * 1.05, t + 0.1);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.1, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(t); osc.stop(t + 0.16);
  });
}

function playAccessGranted(ctx: AudioContext) {
  const now = ctx.currentTime;
  const freqs = [440, 554, 659, 880];
  freqs.forEach((freq, i) => {
    const t = now + i * 0.12;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.12, t + 0.02);
    gain.gain.setValueAtTime(0.12, t + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(t); osc.stop(t + 0.52);
  });
  const sparkle = ctx.createOscillator();
  const sparkleGain = ctx.createGain();
  sparkle.type = 'sine';
  sparkle.frequency.setValueAtTime(1760, now + 0.5);
  sparkleGain.gain.setValueAtTime(0, now + 0.5);
  sparkleGain.gain.linearRampToValueAtTime(0.1, now + 0.52);
  sparkleGain.gain.exponentialRampToValueAtTime(0.001, now + 1.0);
  sparkle.connect(sparkleGain); sparkleGain.connect(ctx.destination);
  sparkle.start(now + 0.5); sparkle.stop(now + 1.02);
}

function playQuestStartSounds() {
  try {
    const ctx = new AudioContext();
    playCrystalForm(ctx);
    setTimeout(() => playHoloMaterialize(ctx), 1500);
    setTimeout(() => playAccessGranted(ctx), 3500);
  } catch (e) {
    // silently ignore if audio context blocked
  }
}

// ─── Types ───

interface StoryLoadingProps {
  trigger: boolean;
  onComplete?: () => void;
  questLabel?: string;
  questTitle?: string;
}

// ─── Constants ───

const DURATION = 6;
const COLS = 12;
const ROWS = 8;

// ─── Component ───

const StoryLoading = ({ trigger, onComplete, questLabel, questTitle }: StoryLoadingProps) => {
  const { primaryColor } = useColor();

  const hexCells = useMemo(() => {
    const cells: { id: number; col: number; row: number; delay: number }[] = [];
    const cx = COLS / 2;
    const cy = ROWS / 2;
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const d = Math.sqrt((c - cx) ** 2 + (r - cy) ** 2);
        cells.push({ id: r * COLS + c, col: c, row: r, delay: d * 0.08 });
      }
    }
    return cells;
  }, []);

  useEffect(() => {
    if (trigger) {
      playQuestStartSounds();
      const timer = setTimeout(() => {
        onComplete?.();
      }, DURATION * 1000);
      return () => clearTimeout(timer);
    }
  }, [trigger]);

  return (
  
    <AnimatePresence>
      {trigger && (
        <motion.div
          key="story-loading"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999999,
            backgroundColor: "#000",
            overflow: "hidden",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}  // ← keep short
        >
          {/* Hex Grid */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div style={{ width: "90vw", height: "80vh", position: "relative" }}>
              {hexCells.map((cell) => {
                const w = 100 / COLS;
                const h = 100 / ROWS;
                const ox = cell.row % 2 === 0 ? 0 : w / 2;
                return (
                  <motion.div
                    key={cell.id}
                    style={{
                      position: "absolute",
                      left: `${cell.col * w + ox}%`,
                      top: `${cell.row * h}%`,
                      width: `${w}%`,
                      height: `${h}%`,
                    }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: [0, 1, 1, 0], scale: [0, 1, 1, 0.8] }}
                    transition={{ duration: 4, delay: cell.delay }}
                  >
                    <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%" }}>
                      <motion.polygon
                        points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5"
                        fill="transparent"
                        stroke={primaryColor}
                        strokeWidth="1"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: [0, 1, 1, 0] }}
                        transition={{ duration: 3, delay: cell.delay }}
                        style={{ filter: `drop-shadow(0 0 8px ${primaryColor})` }}
                      />
                    </svg>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Central glow */}
          <motion.div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${primaryColor}99 0%, transparent 70%)`,
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 2, 4, 6], opacity: [0, 0.8, 0.4, 0] }}
            transition={{ duration: 4, delay: 1 }}
          />

          {/* Quest Label + Title */}
          <motion.div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
            animate={{ opacity: [0, 0, 1, 1, 0] }}
            transition={{ duration: DURATION, times: [0, 0.3, 0.45, 0.8, 1] }}
          >
            <motion.div
              style={{
                color: primaryColor,
                fontFamily: "monospace",
                fontSize: 25,
                letterSpacing: "0.5em",
                marginBottom: 16,
              }}
              animate={{ y: [20, 20, 0, 0], opacity: [0, 0, 1, 1] }}
              transition={{ duration: DURATION, times: [0, 0.3, 0.45, 1] }}
            >
              {questLabel}
            </motion.div>

            <motion.h2
              style={{
                fontSize: "clamp(3rem, 8vw, 6rem)",
                fontWeight: "bold",
                color: primaryColor,
                letterSpacing: "0.1em",
                fontFamily: "monospace",
                textShadow: `0 0 30px ${primaryColor}, 0 0 60px ${primaryColor}66`,
                margin: 0,
              }}
              animate={{ scale: [0.8, 0.8, 1, 1], opacity: [0, 0, 1, 1] }}
              transition={{ duration: DURATION, times: [0, 0.3, 0.45, 1] }}
            >
              {questTitle}
            </motion.h2>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StoryLoading;